import { ListOrdered, type LucideIcon } from 'lucide-react';
import type { PillField } from '@/components/common/LabeledPill';
import type { ViewedRowList } from '@/store/lastViewedRow.store';
import { formatNumber } from '@/utils/number';
import {
  MONITOR_LEVEL_TITLE,
  MONITOR_SCREEN_PATH,
  type MonitorScreen,
  type MonitorScreenPath,
} from '../constants/question.constants';
import type {
  MonitorBoardBreakdownSearch,
  MonitorInstitutesSearch,
  MonitorSearch,
  SubjectListType,
} from '../types/question.types';

const MONITOR_SCREEN_KEYS = ['member', 'byBoard', 'byClass'] as const satisfies MonitorScreen[];

export const getMonitorScreenByPath = (path: MonitorScreenPath): MonitorScreen =>
  MONITOR_SCREEN_KEYS.find((screen) => MONITOR_SCREEN_PATH[screen] === path) ?? 'member';

export const toBoardBreakdownParams = ({
  from: _from,
  ...params
}: MonitorBoardBreakdownSearch): MonitorSearch => params;

export const toInstituteListParams = ({
  from: _from,
  variant: _variant,
  ...params
}: MonitorInstitutesSearch): MonitorSearch => params;

interface ViewedMonitorRow {
  list: ViewedRowList;
  rowId: number | null;
  parentId: number | null;
}

export function getInstituteListParentRow({
  variant,
  userId,
  classId,
  zillaId,
  boardId,
}: MonitorInstitutesSearch): ViewedMonitorRow {
  const boardParentId = boardId ?? null;
  if (variant === 'byUser') {
    return { list: 'questionMonitorMembers', rowId: userId ?? null, parentId: null };
  }
  if (classId !== undefined) {
    return { list: 'questionMonitorClasses', rowId: classId, parentId: boardParentId };
  }
  if (zillaId !== undefined) {
    return { list: 'questionMonitorDistricts', rowId: zillaId, parentId: boardParentId };
  }
  return { list: 'questionMonitorBoards', rowId: boardParentId, parentId: null };
}

interface CountPill {
  key: string;
  label: string;
  count: number | undefined;
  isLoading: boolean;
  icon?: LucideIcon | undefined;
}

export const toCountPillField = ({
  key,
  label,
  count,
  isLoading,
  icon = ListOrdered,
}: CountPill): PillField => ({
  key,
  icon,
  label,
  value: count === undefined ? undefined : formatNumber(count),
  isLoading,
});

export const getInstituteListTitle = (isNew: boolean | undefined): string =>
  isNew ? MONITOR_LEVEL_TITLE.newCollected : MONITOR_LEVEL_TITLE.assigned;

export function getSubjectListTitle(
  subjectType: SubjectListType,
  isNew: boolean | undefined,
): string {
  if (subjectType === 'A') return MONITOR_LEVEL_TITLE.assigned;
  return isNew ? MONITOR_LEVEL_TITLE.newCollected : MONITOR_LEVEL_TITLE.collected;
}
